import api from "@/lib/api";
import { PROTECTED } from "@/lib/noTranslate";

const ATTRS = ["placeholder", "title", "aria-label"];
const SKIP = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "CODE", "svg"]);
const hasLetters = (s) => /\p{L}{2,}/u.test(s);

let lang = "sq";
let observer = null;
let timer = null;
const cache = {};
const origText = new WeakMap();
const applied = new WeakMap();
const origAttr = new WeakMap();
const pending = new Set();
const asked = new Set();

const skipEl = (el) => !el || el.closest?.("[translate='no']:not(html),[data-no-translate],.leaflet-control-attribution,[data-sonner-toaster]") || SKIP.has(el.tagName);

function translateOf(src) {
  const m = cache[lang] || {};
  const key = src.trim();
  if (m[key] == null) { if (!asked.has(`${lang}|${key}`)) pending.add(key); return null; }
  return src.replace(key, m[key]);
}

function scan() {
  if (lang === "sq") return;
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  let n;
  while ((n = walker.nextNode())) {
    const v = n.nodeValue;
    if (!v || !hasLetters(v) || skipEl(n.parentElement) || PROTECTED.has(v.trim())) continue;
    if (applied.get(n) === v) continue;
    origText.set(n, v);
    const t = translateOf(v);
    if (t != null && t !== v) { applied.set(n, t); n.nodeValue = t; } else if (t != null) applied.set(n, v);
  }
  document.body.querySelectorAll("[placeholder],[title],[aria-label]").forEach((el) => {
    if (skipEl(el)) return;
    const o = origAttr.get(el) || {};
    ATTRS.forEach((a) => {
      const v = el.getAttribute(a);
      if (!v || !hasLetters(v) || o[`${a}_t`] === v || PROTECTED.has(v.trim())) return;
      o[a] = v;
      const t = translateOf(v);
      if (t != null) { o[`${a}_t`] = t; el.setAttribute(a, t); }
    });
    origAttr.set(el, o);
  });
  if (pending.size) fetchPending();
}

let inflight = 0;
async function fetchPending() {
  const target = lang;
  while (pending.size && inflight < 4) {
    const batch = [...pending].slice(0, 45);
    batch.forEach((b) => { pending.delete(b); asked.add(`${target}|${b}`); });
    inflight += 1;
    api.post("/i18n/translate", { lang: target, texts: batch })
      .then((r) => { cache[target] = { ...(cache[target] || {}), ...r.data.map }; })
      .catch(() => {})
      .finally(() => {
        inflight -= 1;
        batch.forEach((b) => { if (cache[target]?.[b] == null) cache[target] = { ...(cache[target] || {}), [b]: b }; });
        if (lang === target) scan();
      });
  }
}

function restore() {
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  let n;
  while ((n = walker.nextNode())) {
    if (applied.has(n) && applied.get(n) === n.nodeValue && origText.has(n)) n.nodeValue = origText.get(n);
    applied.delete(n);
  }
  document.body.querySelectorAll("[placeholder],[title],[aria-label]").forEach((el) => {
    const o = origAttr.get(el);
    if (!o) return;
    ATTRS.forEach((a) => { if (o[a] && el.getAttribute(a) === o[`${a}_t`]) el.setAttribute(a, o[a]); });
    origAttr.delete(el);
  });
}

const listeners = new Set();
export const onLanguageChange = (cb) => { listeners.add(cb); return () => listeners.delete(cb); };

export async function translateTexts(list) {
  if (lang === "sq") return list;
  const target = lang;
  const m = cache[target] || {};
  const miss = list.filter((t) => t && m[t] == null);
  if (miss.length) {
    try { const r = await api.post("/i18n/translate", { lang: target, texts: miss }); cache[target] = { ...(cache[target] || {}), ...r.data.map }; } catch { /* keep originals */ }
  }
  return list.map((t) => cache[target]?.[t] ?? t);
}

export function setLanguage(next) {
  if (next === lang) return;
  restore();
  lang = next;
  pending.clear();
  listeners.forEach((cb) => cb(next));
  document.documentElement.lang = next === "no" ? "nb" : next;
  if (observer) { observer.disconnect(); observer = null; }
  if (next === "sq") return;
  observer = new MutationObserver(() => { clearTimeout(timer); timer = setTimeout(scan, 120); });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  scan();
}

export const getLanguage = () => lang;
